import {
  DealPipelineInvestmentWorkflowStateFiltersQuery,
  FatDealCatalogPageInvestmentsByWorkflowStateQuery,
  FatDealCatalogPageOpenOpportunitiesQuery,
  InvestorInterestListSortBy,
  PaginateSortDirection
} from '@/__generated__/graphql';
import type { ITab } from '@/components/basicComponents/tabs';
import { BurgerMenuIcon } from '@/assets/static/icons/burger-menu';
import { CloseMenuIcon } from '@/assets/static/icons/close-menu';
import Tabs, {
  CounterTitle,
  MainTabsMobile,
  TabMobileWrap,
  TabTitle,
  TabsControl,
  TabsDetailsTitleMobile,
  TabsWrapMobile
} from '@/components/basicComponents/tabs';
import { useResponsive } from '@/hooks/use-responsive';
import { useCustomizationsData } from '@/hooks/useCustomizationsData';
import { MainWrap } from '@/styles/common';
import { themeVar } from '@/styles/theme';
import { useQuery } from '@apollo/client';
import { useEffect, useState } from 'react';
import { Helmet as Head } from 'react-helmet';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import SpecialControlHeader from '../../components/dealCatalog-page/switcher/specialControlHeader';
import { IInvestorInterestList, TypeWindow } from '../../components/dealCatalog-page/types';
import { IFilterBarData } from '../basicComponents/filterBar';
import { ISelectValue } from '../basicComponents/select';
import { createSimpleSelectOption } from '../basicComponents/select/utils';
import Header from '../header';
import { IMobileSort } from '../pendingCommitments-page/types';
import { GET_ASSET_CLASSES_QUERY } from '../settings-page/assetClassDefaultTargets/queries';
import { getCacheBustedFileUrl } from '../settings-page/utils';
import { initialValuesFromTable } from '../table/constants';
import FilterPage from '../table/filterPage';
import TablePagination from '../table/tablePagination';
import { ISort, SORT_ORDER } from '../table/types';
import { initialStatusFilter, opportunitiesTableSort, seekingInterestTableSort, workflowStatesMap } from './constants';
import { default as OpportunitiesTable } from './opportunitiesTable/table';
import RecommendedTable from './recommendedTable/table';
import { INVESTMENTS_BY_WORKFLOW_STATE_QUERY, OPEN_OPPORTUNITIES_QUERY } from './queries';
import SeekingInterestTable from './seekingInterestTable/table';
import { DEAL_PIPELINE_INVESTMENT_WORKFLOW_STATE_FILTERS_QUERY } from '../dealPipeline-page/queries';

const getStatusFilterItems = (filterBar: IFilterBarData[], activeTab: string) => {
  const filterBarEnabledValues = filterBar.filter((item) => item.enabled && item.type === activeTab).map((item) => item.value);

  if (!filterBarEnabledValues.length) {
    return filterBar.filter((item) => item.type === activeTab).map((item) => item.value);
  }
  return filterBarEnabledValues;
};

interface ICatalogBannerCustomizations {
  bannerImageLogo: string;
  bannerImageBackground: string;
  pageTitle: string;
  bannerImageText: string;
  headline: string;
  tagline: string;
}

const defaultCatalogBannerCustomizationsData: ICatalogBannerCustomizations = {
  bannerImageLogo: '',
  bannerImageBackground: '',
  pageTitle: '',
  bannerImageText: '',
  headline: '',
  tagline: ''
};

const ACCEPTING_COMMITMENTS_TAB_VALUE = 'Accepting Commitments';

const WORKFLOW_TAB_ORDER: Array<'seekingInterest' | 'recommended' | 'dueDiligence'> = ['seekingInterest', 'recommended', 'dueDiligence'];

const ACCEPTING_COMMITMENTS_TAB: ITab = {
  value: ACCEPTING_COMMITMENTS_TAB_VALUE,
  label: ACCEPTING_COMMITMENTS_TAB_VALUE,
  amount: null
};

export const DealCatalog = () => {
  const { isMobile, isTablet, isDesktop, isLargeDesktop } = useResponsive();
  const { data: customizationsData } = useCustomizationsData();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const activeTab = searchParams.get('tab') || ACCEPTING_COMMITMENTS_TAB_VALUE;
  const q = searchParams.get('q') || '';
  const assetClassQuickFilter = searchParams.get('primaryFilter') || '';
  const key = searchParams.get('key') || getDefaultSortKey();
  const asc = searchParams.get('asc') === null || searchParams.get('asc') === 'true';
  const limit = searchParams.get('limit') || initialValuesFromTable.initialAmountRowsPerPage[0];
  const offset = searchParams.get('offset') || 0;
  const filterBarParams = searchParams.get('filterBar');
  const position = searchParams.get('position') || 0;

  const [switcher, setSwitcher] = useState(true);
  const [isTabMenuOpen, setIsTabMenuOpen] = useState(false);

  const [isLoading, setIsLoading] = useState(true);
  const [filteredTabs, setFilteredTabs] = useState<ITab[]>([]);

  const { data: investmentWorkflowStateFiltersData } = useQuery<{
    InvestmentWorkflowStateFilters: DealPipelineInvestmentWorkflowStateFiltersQuery['InvestmentWorkflowStateFilters'];
  }>(DEAL_PIPELINE_INVESTMENT_WORKFLOW_STATE_FILTERS_QUERY);

  useEffect(() => {
    if (investmentWorkflowStateFiltersData?.InvestmentWorkflowStateFilters) {
      const filters = investmentWorkflowStateFiltersData.InvestmentWorkflowStateFilters;
      const workflowTabs: ITab[] = WORKFLOW_TAB_ORDER.map((name) => filters.find((f) => f.name === name))
        .filter(Boolean)
        .map((filter) => ({
          value: filter!.name,
          label: filter!.label,
          amount: null as number | null
        }));
      const availableTabs = [ACCEPTING_COMMITMENTS_TAB, ...workflowTabs];

      setFilteredTabs(availableTabs);
      setIsLoading(false);

      if (availableTabs.length > 0 && !availableTabs.some((tab) => tab.value === activeTab)) {
        setSearchParams({ tab: availableTabs[0].value }, { replace: true });
      }
    }
  }, [investmentWorkflowStateFiltersData, activeTab, setSearchParams]);

  const [openOpportunitiesTableData, setOpenOpportunitiesTableData] = useState<FatDealCatalogPageOpenOpportunitiesQuery['OpenOpportunities']['data']>(
    []
  );
  const [seekingInterestTableData, setSeekingInterestTableData] = useState<IInvestorInterestList[]>([]);
  const [recommendedData, setRecommendedData] = useState<IInvestorInterestList[]>([]);
  const [dueDiligenceData, setDueDiligenceData] = useState<IInvestorInterestList[]>([]);

  const [filterBar, setFilterBar] = useState<IFilterBarData[]>(filterBarData());
  const [assetClassSelectOptions, setAssetClassSelectOptions] = useState<ISelectValue[]>([]);

  const [mobileSortItems, setMobileSortItems] = useState<IMobileSort[]>([]);
  const [mobileSort, setMobileSort] = useState('');
  const mobileSortList = mobileSortItems.map((item) => item.title);

  const [catalogBannerCustomizationsData, setCatalogBannerCustomizationsData] = useState<ICatalogBannerCustomizations>(
    defaultCatalogBannerCustomizationsData
  );

  function filterBarData() {
    if (!filterBarParams) return initialStatusFilter;
    const matchedFilterBarValues = initialStatusFilter.map((item) => {
      const matchedFilterItem = JSON.parse(filterBarParams).find((filterItem: string) => item.type === activeTab && filterItem === item.value);

      return {
        ...item,
        enabled: Boolean(matchedFilterItem)
      };
    });
    return matchedFilterBarValues;
  }

  function getDefaultSortKey() {
    if (activeTab === ACCEPTING_COMMITMENTS_TAB_VALUE) {
      return 'INVESTMENT_TYPE';
    }

    return 'INVESTOR_DOCS_DUE';
  }

  useEffect(() => {
    if (!isMobile) return;
    if (activeTab === ACCEPTING_COMMITMENTS_TAB_VALUE) {
      setMobileSortItems(opportunitiesTableSort);
      setMobileSort(opportunitiesTableSort[5].title);
      return;
    }
    if (activeTab === 'seekingInterest') {
      setMobileSortItems(seekingInterestTableSort);
      setMobileSort(seekingInterestTableSort[6].title);
      return;
    }
    if (activeTab === 'recommended' || activeTab === 'dueDiligence') {
      setMobileSortItems([...seekingInterestTableSort].splice(0, seekingInterestTableSort.length - 2));
      setMobileSort(seekingInterestTableSort[6].title);
      return;
    }
  }, [activeTab, isMobile]);

  useEffect(() => {
    const { pageTitle, bannerImageText, headline, tagline, bannerImageLogoUrl, bannerImageBackgroundUrl } =
      customizationsData?.theme?.catalogBanner ?? {};

    setCatalogBannerCustomizationsData({
      bannerImageLogo: bannerImageLogoUrl,
      bannerImageBackground: bannerImageBackgroundUrl,
      pageTitle,
      bannerImageText,
      headline,
      tagline
    });
  }, [customizationsData]);

  const { data: assetClassesData } = useQuery(GET_ASSET_CLASSES_QUERY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
    onCompleted: ({ assetClasses }) => {
      setAssetClassSelectOptions(assetClasses.map((assetClass) => createSimpleSelectOption(assetClass.id, assetClass.name)));
    },
    onError: (error) => {
      console.error(error);
    }
  });

  const {
    data: openOpportunitiesData,
    loading: openOpportunitiesLoading,
    refetch: openOpportunitiesRefetch
  } = useQuery(OPEN_OPPORTUNITIES_QUERY, {
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    skip: activeTab !== ACCEPTING_COMMITMENTS_TAB_VALUE || !assetClassesData,
    variables: {
      sort: key,
      order: asc ? SORT_ORDER.ASC : SORT_ORDER.DESC,
      search: q.toLowerCase().trim(),
      rowsPerPage: Number(limit),
      page: Math.floor(Number(offset) / Number(limit)) + 1,
      assetClassQuickFilter: assetClassQuickFilter,
      statusFilter: getStatusFilterItems(filterBar, ACCEPTING_COMMITMENTS_TAB_VALUE) as string[]
    },
    onCompleted: (data) => {
      const { data: openOpportunitiesData, filters } = data.OpenOpportunities;

      setOpenOpportunitiesTableData(openOpportunitiesData);

      const filterBarWithCounts = filterBar.map((filterBarItem) => {
        const matchedFilterItem = filters.find((filterItem: any) => filterItem.value === filterBarItem.value);

        return { ...filterBarItem, count: matchedFilterItem?.count ?? 0 };
      });

      setFilterBar(filterBarWithCounts);
      makeScroll(position);
    }
  });

  const {
    data: seekingInterestData,
    refetch: seekingInterestRefetch,
    loading: seekingInterestLoading,
    networkStatus: seekingInterestNetworkStatus
  } = useQuery(INVESTMENTS_BY_WORKFLOW_STATE_QUERY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'no-cache',
    skip: activeTab !== workflowStatesMap.seekingInterest || !assetClassesData,
    variables: {
      data: {
        sortBy: key as InvestorInterestListSortBy,
        sortDirection: asc ? PaginateSortDirection.Asc : PaginateSortDirection.Desc,
        assetClassFilter: !!assetClassQuickFilter ? [assetClassQuickFilter] : [],
        search: q.toLowerCase().trim(),
        limit: Number(limit),
        offset: Number(offset),
        workflowStatesFilter: [workflowStatesMap.seekingInterest]
      }
    },
    onCompleted: (data) => {
      const { data: seekingInterestData, filters } = data.InvestmentsByWorkflowState;

      setSeekingInterestTableData(seekingInterestData as IInvestorInterestList[]);

      const filterBarWithCounts = filterBar.map((filterBarItem) => {
        const matchedFilterItem = filters?.find((filterItem) => filterItem.value === filterBarItem.value);

        return { ...filterBarItem, count: matchedFilterItem?.count ?? 0 };
      });

      setFilterBar(filterBarWithCounts);
      makeScroll(position);
    },
    onError: (error) => {
      console.error(error);
    }
  });

  const {
    data: recommendedInvestmentsData,
    refetch: recommendedInvestmentsRefetch,
    loading: recommendedInvestmentsLoading
  } = useQuery(INVESTMENTS_BY_WORKFLOW_STATE_QUERY, {
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    skip: activeTab !== workflowStatesMap.recommended || !assetClassesData,
    variables: {
      data: {
        sortBy: key as InvestorInterestListSortBy,
        sortDirection: asc ? PaginateSortDirection.Asc : PaginateSortDirection.Desc,
        assetClassFilter: !!assetClassQuickFilter ? [assetClassQuickFilter] : [],
        search: q.toLowerCase().trim(),
        limit: Number(limit),
        offset: Number(offset),
        workflowStatesFilter: [workflowStatesMap.recommended]
      }
    },
    onCompleted: (data) => {
      const { data: recommendedInvestmentsData, filters } = data.InvestmentsByWorkflowState;

      setRecommendedData(recommendedInvestmentsData as IInvestorInterestList[]);

      const filterBarWithCounts = filterBar.map((filterBarItem) => {
        const matchedFilterItem = filters?.find((filterItem) => filterItem.value === filterBarItem.value);

        return { ...filterBarItem, count: matchedFilterItem?.count ?? 0 };
      });

      setFilterBar(filterBarWithCounts);
      makeScroll(position);
    },
    onError: (error) => {
      console.error(error);
    }
  });

  const {
    data: dueDiligenceInvestmentsData,
    refetch: dueDiligenceInvestmentsRefetch,
    loading: dueDiligenceInvestmentsLoading
  } = useQuery(INVESTMENTS_BY_WORKFLOW_STATE_QUERY, {
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    skip: activeTab !== workflowStatesMap.dueDiligence || !assetClassesData,
    variables: {
      data: {
        sortBy: key as InvestorInterestListSortBy,
        sortDirection: asc ? PaginateSortDirection.Asc : PaginateSortDirection.Desc,
        assetClassFilter: !!assetClassQuickFilter ? [assetClassQuickFilter] : [],
        search: q.toLowerCase().trim(),
        limit: Number(limit),
        offset: Number(offset),
        workflowStatesFilter: [workflowStatesMap.dueDiligence]
      }
    },
    onCompleted: (data) => {
      const { data: inResearchData, filters } = data.InvestmentsByWorkflowState;

      setDueDiligenceData(inResearchData as IInvestorInterestList[]);

      const filterBarWithCounts = filterBar.map((filterBarItem) => {
        const matchedFilterItem = filters?.find((filterItem) => filterItem.value === filterBarItem.value);

        return { ...filterBarItem, count: matchedFilterItem?.count ?? 0 };
      });
      setFilterBar(filterBarWithCounts);
      makeScroll(position);
    },
    onError: (error) => {
      console.error(error);
    }
  });

  const toggleSwitcher = () => {
    saveScrollPostion();
    setSwitcher(!switcher);
  };

  const openPage = (id: string, type: TypeWindow) => {
    setSearchParams(
      (prev) => {
        prev.set('position', window.scrollY.toString());
        return prev;
      },
      { replace: true }
    );

    if (type === 'opportunityDetails') {
      navigate(`/strategyDetails/${id}?backToTitle=Deal Catalog`);
      return;
    }
    navigate(`${type}/${id}?backToTitle=Deal Catalog`);
  };

  const getCurrentStatus = () => {
    switch (activeTab) {
      case ACCEPTING_COMMITMENTS_TAB_VALUE:
        return {
          refetch: openOpportunitiesRefetch,
          total: openOpportunitiesData?.OpenOpportunities.total
        };
      case workflowStatesMap.seekingInterest:
        return {
          refetch: seekingInterestRefetch,
          total: seekingInterestData?.InvestmentsByWorkflowState.total
        };
      case workflowStatesMap.recommended:
        return {
          refetch: recommendedInvestmentsRefetch,
          total: recommendedInvestmentsData?.InvestmentsByWorkflowState.total
        };
      case workflowStatesMap.dueDiligence:
        return {
          refetch: dueDiligenceInvestmentsRefetch,
          total: dueDiligenceInvestmentsData?.InvestmentsByWorkflowState.total
        };
      default:
        return {
          refetch: () => null as any,
          total: 0
        };
    }
  };

  const onChangePrimaryFilter = (filter: string) => {
    setSearchParams(
      (prev) => {
        prev.delete('limit');
        prev.delete('offset');
        if (!filter) {
          prev.delete('primaryFilter');
          return;
        }
        prev.set('primaryFilter', filter);
        return prev;
      },
      { replace: true }
    );
  };

  const onChangeSearch = (searchValue: string) => {
    setSearchParams(
      (prev) => {
        if (!searchValue) {
          prev.delete('q');
          return prev;
        }
        prev.set('q', searchValue);
        prev.delete('limit');
        prev.delete('offset');
        return prev;
      },
      { replace: true }
    );
  };

  const onChangeSort = (value: ISort) => {
    setSearchParams(
      (prev) => {
        prev.set('key', value.key);
        prev.set('asc', value.asc.toString());
        return prev;
      },
      { replace: true }
    );
  };

  const saveScrollPostion = () => {
    setSearchParams(
      (prev) => {
        prev.set('position', window.scrollY.toString());
        return prev;
      },
      { replace: true }
    );
  };

  const makeScroll = (position: string | number) => {
    if (!position) return;
    setTimeout(() => window.scrollTo({ top: Number(position), behavior: 'smooth' }), 0);
    setSearchParams(
      (prev) => {
        prev.delete('position');
        return prev;
      },
      { replace: true }
    );
  };

  const onChangeFilterBar = (filterBarItems: IFilterBarData[]) => {
    const updatedFilterBar = initialStatusFilter.map((item) => {
      const updatedItem = filterBarItems.find((filterItem) => filterItem.type === item.type && filterItem.value === item.value);

      return updatedItem ? updatedItem : item;
    });

    setFilterBar(updatedFilterBar);
    const filterBarValues = filterBarItems.filter((item) => item.enabled).map((item) => item.value);
    setSearchParams(
      (prev) => {
        if (!filterBarValues.length) {
          prev.delete('filterBar');
          return prev;
        }
        prev.set('filterBar', JSON.stringify(filterBarValues));
        prev.delete('limit');
        prev.delete('offset');
        return prev;
      },
      { replace: true }
    );
  };

  const onChangePaginationData = (limit: number, offset: number) => {
    setSearchParams(
      (prev) => {
        prev.set('limit', limit.toString());
        prev.set('offset', offset.toString());
        return prev;
      },
      { replace: true }
    );
  };

  const onChangeActiveTab = (value: string) => {
    if (value === activeTab) return;
    setFilterBar(initialStatusFilter);
    setSearchParams({ tab: value }, { replace: true });
  };

  const onChangeMobileSort = (value: string) => {
    setMobileSort(value);
    const sortItem = mobileSortItems.find((item) => item.title === value);

    if (activeTab === ACCEPTING_COMMITMENTS_TAB_VALUE) {
      openOpportunitiesRefetch({
        order: sortItem?.asc ? SORT_ORDER.ASC : SORT_ORDER.DESC,
        sort: sortItem?.key
      });
      return;
    }
    if (activeTab === workflowStatesMap.seekingInterest) {
      seekingInterestRefetch({
        data: {
          sortDirection: sortItem?.asc ? PaginateSortDirection.Asc : PaginateSortDirection.Desc,
          sortBy: sortItem?.key as InvestorInterestListSortBy,
          assetClassFilter: !!assetClassQuickFilter ? [assetClassQuickFilter] : [],
          search: q.toLowerCase().trim(),
          limit: Number(limit),
          offset: Number(offset),
          workflowStatesFilter: [workflowStatesMap.seekingInterest]
        }
      });
      return;
    }
    if (activeTab === workflowStatesMap.recommended) {
      recommendedInvestmentsRefetch({
        data: {
          sortDirection: sortItem?.asc ? PaginateSortDirection.Asc : PaginateSortDirection.Desc,
          sortBy: sortItem?.key as InvestorInterestListSortBy,
          assetClassFilter: !!assetClassQuickFilter ? [assetClassQuickFilter] : [],
          search: q.toLowerCase().trim(),
          limit: Number(limit),
          offset: Number(offset),
          workflowStatesFilter: [workflowStatesMap.recommended]
        }
      });
      return;
    }
    if (activeTab === workflowStatesMap.dueDiligence) {
      dueDiligenceInvestmentsRefetch({
        data: {
          sortDirection: sortItem?.asc ? PaginateSortDirection.Asc : PaginateSortDirection.Desc,
          sortBy: sortItem?.key as InvestorInterestListSortBy,
          assetClassFilter: !!assetClassQuickFilter ? [assetClassQuickFilter] : [],
          search: q.toLowerCase().trim(),
          limit: Number(limit),
          offset: Number(offset),
          workflowStatesFilter: [workflowStatesMap.dueDiligence]
        }
      });
      return;
    }
  };

  const changeTab = (value: string) => {
    onChangeActiveTab(value);
    setIsTabMenuOpen(false);
  };

  return (
    <>
      <Head>
        <title>{catalogBannerCustomizationsData.pageTitle || `${process.env.REACT_APP_PLATFORM_NAME}`}</title>
      </Head>
      <MainWrap>
        <Header
          {...(catalogBannerCustomizationsData.pageTitle && {
            title: catalogBannerCustomizationsData.pageTitle
          })}
          specialControl={<SpecialControlHeader switcher={switcher} setSwitcher={toggleSwitcher} title="Include Pending Commitments" hide={true} />}
        />
        <PaddingWrap>
          {(catalogBannerCustomizationsData.bannerImageBackground ||
            catalogBannerCustomizationsData.bannerImageLogo ||
            catalogBannerCustomizationsData.bannerImageText ||
            catalogBannerCustomizationsData.headline ||
            catalogBannerCustomizationsData.tagline) && (
            <ImageWrap withBannerImageBackground={!!catalogBannerCustomizationsData.bannerImageBackground} isMobile={isMobile}>
              {catalogBannerCustomizationsData.bannerImageBackground && (
                <ImageBackground
                  isFullWidth={!catalogBannerCustomizationsData.bannerImageLogo && !catalogBannerCustomizationsData.bannerImageText}
                  isMobile={isMobile}
                  isTablet={isTablet}
                >
                  <CurioImg
                    isMobile={isMobile}
                    src={getCacheBustedFileUrl(catalogBannerCustomizationsData.bannerImageBackground)}
                    draggable="false"
                    alt="Banner Image Background"
                  />
                </ImageBackground>
              )}

              {!isMobile && !isTablet && (!!catalogBannerCustomizationsData.bannerImageLogo || !!catalogBannerCustomizationsData.bannerImageText) && (
                <DescriptionBlockWrapper withBannerImageBackground={!!catalogBannerCustomizationsData.bannerImageBackground}>
                  {!catalogBannerCustomizationsData.bannerImageBackground && <DecorationBlock />}
                  <DescriptionBlock>
                    {catalogBannerCustomizationsData.bannerImageLogo && (
                      <div>
                        <CurioLogoImg
                          src={getCacheBustedFileUrl(catalogBannerCustomizationsData.bannerImageLogo)}
                          draggable="false"
                          alt="Banner Image Logo"
                        />
                      </div>
                    )}
                    {catalogBannerCustomizationsData.bannerImageText && (
                      <DescriptionText>{catalogBannerCustomizationsData.bannerImageText}</DescriptionText>
                    )}
                  </DescriptionBlock>
                </DescriptionBlockWrapper>
              )}
            </ImageWrap>
          )}

          {catalogBannerCustomizationsData.headline && (
            <ManagerPipelineBanner isMobile={isMobile}>{catalogBannerCustomizationsData.headline}</ManagerPipelineBanner>
          )}
          {catalogBannerCustomizationsData.tagline && (
            <InformationBanner isMobile={isMobile}>{catalogBannerCustomizationsData.tagline}</InformationBanner>
          )}

          {!isLoading && (
            <>
              {filteredTabs.length === 1 ? (
                <></>
              ) : isDesktop || isTablet || isLargeDesktop ? (
                <>
                  <Tabs tabs={filteredTabs} activeTab={activeTab} setActiveTab={onChangeActiveTab} />
                  <TabsLine />
                </>
              ) : (
                <CustomTabsControl isMobile={isMobile}>
                  {filteredTabs.length !== 1 && (
                    <>
                      {isTabMenuOpen ? (
                        <CloseMenuIcon
                          fill={themeVar('action.primary')}
                          onClick={() => setIsTabMenuOpen(!isTabMenuOpen)}
                          style={{ cursor: 'pointer' }}
                        />
                      ) : (
                        <BurgerMenuIcon
                          fill={themeVar('action.primary')}
                          onClick={() => setIsTabMenuOpen(!isTabMenuOpen)}
                          style={{ cursor: 'pointer' }}
                        />
                      )}
                    </>
                  )}
                  <TabMobileWrap onClick={() => onChangeActiveTab(activeTab)} isActive>
                    <TabTitle isActive isMobile={isMobile}>
                      {filteredTabs.find((t) => t.value === activeTab)?.label ?? activeTab}
                    </TabTitle>
                  </TabMobileWrap>
                </CustomTabsControl>
              )}
            </>
          )}
        </PaddingWrap>
      </MainWrap>
      <MainWrap>
        <PaddingWrap>
          {!isLoading && (
            <>
              {filteredTabs.length === 1 ? (
                <></>
              ) : (
                isTabMenuOpen &&
                isMobile && (
                  <MainTabsMobile>
                    {filteredTabs.map((tab, tabId) => (
                      <TabsWrapMobile key={tab.value + tabId} onClick={() => changeTab(tab.value)} isActive={activeTab === tab.value}>
                        {tab.amount && <CounterTitle isActive={activeTab === tab.value}>{tab.amount}</CounterTitle>}
                        <TabsDetailsTitleMobile isActive={activeTab === tab.value}>{tab.label ?? tab.value}</TabsDetailsTitleMobile>
                      </TabsWrapMobile>
                    ))}
                  </MainTabsMobile>
                )
              )}
            </>
          )}
          {!isTabMenuOpen && (
            <>
              <FilterPage
                search={{
                  value: q,
                  onChange: onChangeSearch,
                  placeholder: 'Search'
                }}
                quickFilter={{
                  title: 'Asset Class',
                  value: assetClassQuickFilter,
                  defaultValue: 'All Asset Classes',
                  onChange: onChangePrimaryFilter,
                  options: [{ label: 'All Asset Classes', value: '' }, ...assetClassSelectOptions]
                }}
                resultsValue={getCurrentStatus().total ?? 0}
                refetch={getCurrentStatus().refetch}
                {...(activeTab === ACCEPTING_COMMITMENTS_TAB_VALUE && {
                  filterBar: filterBar.filter((item) => item.type === activeTab),
                  onChangeFilterBar: onChangeFilterBar
                })}
                mobileSort={{
                  value: mobileSort,
                  onChange: onChangeMobileSort,
                  selects: mobileSortList
                }}
                useNewFilters
              />
              {activeTab === ACCEPTING_COMMITMENTS_TAB_VALUE && (
                <OpportunitiesTable
                  openPage={openPage}
                  loading={openOpportunitiesLoading}
                  openOpportunitiesData={openOpportunitiesTableData}
                  refetch={openOpportunitiesRefetch}
                  sort={{ key, asc }}
                  setSort={onChangeSort}
                />
              )}
              {activeTab === workflowStatesMap.seekingInterest && (
                <SeekingInterestTable
                  openPage={openPage}
                  refetch={seekingInterestRefetch}
                  seekingInterestNetworkStatus={seekingInterestNetworkStatus}
                  seekingInterestData={seekingInterestTableData}
                  sort={{ key, asc }}
                  setSort={onChangeSort}
                />
              )}
              {activeTab === workflowStatesMap.recommended && (
                <RecommendedTable
                  openPage={openPage}
                  refetch={recommendedInvestmentsRefetch}
                  loading={recommendedInvestmentsLoading}
                  recommendedData={recommendedData}
                  sort={{ key, asc }}
                  setSort={onChangeSort}
                />
              )}
              {activeTab === workflowStatesMap.dueDiligence && (
                <RecommendedTable
                  openPage={openPage}
                  refetch={dueDiligenceInvestmentsRefetch}
                  loading={dueDiligenceInvestmentsLoading}
                  recommendedData={dueDiligenceData}
                  sort={{ key, asc }}
                  setSort={onChangeSort}
                />
              )}
              <PaginationWrap>
                <TablePagination
                  savePagination={onChangePaginationData}
                  paginationValues={{
                    limit: Number(limit),
                    offset: Number(offset),
                    total: getCurrentStatus().total
                  }}
                  refetch={() => null as any}
                  usePage={activeTab === ACCEPTING_COMMITMENTS_TAB_VALUE}
                />
              </PaginationWrap>
            </>
          )}
        </PaddingWrap>
      </MainWrap>
    </>
  );
};

const PaddingWrap = styled.div`
  padding-left: 16px;
  padding-right: 16px;
  @media (min-width: 600px) {
    padding-left: 50px;
    padding-right: 50px;
  }
`;

const CustomTabsControl = styled(TabsControl)`
  margin-top: 25px;
`;

const PaginationWrap = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
`;

const ImageWrap = styled.div<{ withBannerImageBackground: boolean; isMobile: boolean }>`
  margin: 40px 0 20px 0;
  display: flex;
  gap: 20px;
  height: ${({ withBannerImageBackground, isMobile }) => (withBannerImageBackground ? (isMobile ? '300px' : '515px') : 'auto')};
  width: 100%;
  overflow: hidden;
`;

const ImageBackground = styled.div<{ isMobile: boolean; isTablet: boolean; isFullWidth: boolean }>`
  border-left: ${({ isMobile, isTablet }) =>
    isMobile || isTablet ? `20px solid ${themeVar('header.base')}` : `40px solid  ${themeVar('header.base')}`};
  width: ${({ isMobile, isTablet, isFullWidth }) => (isMobile || isTablet || isFullWidth ? '100%' : '70%')};
  position: relative;
`;

const DescriptionBlockWrapper = styled.div<{ withBannerImageBackground: boolean }>`
  display: flex;
  flex-wrap: nowrap;
  gap: 20px;
  width: ${({ withBannerImageBackground }) => (withBannerImageBackground ? '30%' : '100%')};
`;

const DescriptionBlock = styled.div`
  background-color: ${themeVar('header.base')};
  display: flex;
  flex-direction: column;
  gap: 30px;
  width: 100%;
  padding: 60px;
`;

const DecorationBlock = styled.div`
  width: 36px;
  height: 100%;
  background-color: ${themeVar('header.base')};
`;

const DescriptionText = styled.p`
  font-family: Lato;
  font-size: clamp(16px, 1.4vw, 24px);
  line-height: clamp(30px, 1.9vw, 38px);
  font-weight: 300;
  color: ${themeVar('header.action')};
`;

const CurioImg = styled.img<{ isMobile: boolean }>`
  height: 100%;
  border-left: ${({ isMobile, theme }) => (isMobile ? `10px solid ${themeVar('layer.base')}` : `20px solid  ${themeVar('layer.base')}`)};
  object-fit: cover;
  max-width: none;
  width: 100%;
`;

const CurioLogoImg = styled.img`
  min-width: 100px;
  width: auto;
  max-width: 100%;
  max-height: 130px;
  object-fit: contain;
`;

const ManagerPipelineBanner = styled.div<{ isMobile: boolean }>`
  font-family: Lato;
  font-size: ${({ isMobile }) => (isMobile ? '18px' : '30px')};
  font-weight: 900;
  line-height: 39.2px;
  padding: ${({ isMobile }) => (isMobile ? '10px' : '20px')};
  background-color: ${themeVar('menu.active')};
  color: ${themeVar('header.action')};
`;

const InformationBanner = styled.div<{ isMobile: boolean }>`
  font-family: Lato;
  font-size: ${({ isMobile }) => (isMobile ? '13px' : '16px')};
  font-weight: 400;
  line-height: 24px;
  padding: ${({ isMobile }) => (isMobile ? '10px' : '20px')};
  background-color: ${themeVar('menu.tab')};
  color: ${themeVar('header.action')};
  box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.15) inset;
`;

const TabsLine = styled.div`
  border-bottom: 1px ${themeVar('border.base')} solid;
`;
